import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar,
  Trophy,
  Filter,
  Search,
  ChevronRight,
  Clock,
  Users,
  Star,
  Award,
  Target,
  Activity,
  ArrowRight,
  Play,
  CheckCircle2
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { seasonsApi } from '../../services/api';
import { useSite } from '../../contexts/SiteContext';
import { useSeason } from '../../contexts/SeasonContext';

// Types for seasons
interface Season {
  id: string;
  name: string;
  start_year: number;
  end_year: number;
  sport_name: string;
  league_name: string;
  level_name: string;
  status: 'completed' | 'active' | 'upcoming';
  champion?: {
    team_name: string;
    team_city: string;
    team_abbreviation: string;
  };
  start_date: string;
  end_date?: string;
  teams_count?: number;
  games_count?: number;
  championship_name?: string;
  playoffs_format?: string;
  regular_season_games?: number;
}

const SeasonsPage: React.FC = () => {
  const site = useSite();
  const { currentSeason, setCurrentSeason } = useSeason();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDecade, setSelectedDecade] = useState('all');

  // Fetch seasons from API
  const { data: seasonsData, isLoading, error } = useQuery({
    queryKey: ['seasons', site.sport, selectedStatus],
    queryFn: async () => {
      try {
        const response = await seasonsApi.getAll();
        return response.data || [];
      } catch (error) {
        console.error('Error fetching seasons:', error);
        return [];
      }
    },
  });

  // Transform API data to match component interface
  const currentYear = new Date().getFullYear();
  const seasons = {
    data: seasonsData?.map((season: any) => {
      // Determine status based on year
      let status = 'completed';
      if (season.start_year > currentYear) {
        status = 'upcoming';
      } else if (season.start_year === currentYear) {
        // For current year, check if season has dates to be more precise
        if (season.regular_season_start && season.playoffs_end) {
          const today = new Date();
          const seasonStart = new Date(season.regular_season_start);
          const seasonEnd = new Date(season.playoffs_end);
          if (today < seasonStart) {
            status = 'upcoming';
          } else if (today <= seasonEnd) {
            status = 'active';
          } else {
            status = 'completed';
          }
        } else {
          // If no dates, assume current year seasons are active
          status = 'active';
        }
      }
      
      return {
        id: season.id || Math.random().toString(),
        name: season.name || `${season.start_year || 'Unknown'} Season`,
        start_year: season.start_year || new Date().getFullYear(),
        end_year: season.end_year || season.start_year || new Date().getFullYear(),
        sport_name: season.sport?.name || site.sport || 'Sports',
        league_name: season.league?.name || `${site.sport} League`,
        level_name: season.level?.name || 'Professional',
        status,
        champion: season.champion ? {
          team_name: season.champion.team_name || season.champion.name,
          team_city: season.champion.team_city || season.champion.city,
          team_abbreviation: season.champion.team_abbreviation || season.champion.abbreviation
        } : undefined,
        start_date: season.regular_season_start,
        end_date: season.playoffs_end || season.regular_season_end,
        teams_count: season.teams_count,
        games_count: season.games_count,
        championship_name: season.championship_name || season.championship?.name,
        playoffs_format: season.playoffs_format,
        regular_season_games: season.regular_season_games
      };
    }) || []
  };


  // Generate decades dynamically based on available seasons
  const getDecades = () => {
    if (!seasons?.data || seasons.data.length === 0) return [];
    
    const years = seasons.data.map(s => s.start_year);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    
    const decades = [];
    // Start from the most recent decade and go back
    for (let decade = Math.floor(maxYear / 10) * 10; decade >= Math.floor(minYear / 10) * 10; decade -= 10) {
      decades.push({
        value: `${decade}s`,
        label: `${decade}s`,
        startYear: decade,
        endYear: decade + 9
      });
    }
    
    return decades;
  };

  const decades = getDecades();

  const filteredSeasons = seasons?.data?.filter((season: any) => {
    const matchesSearch = 
      season.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      season.sport_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      season.league_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      season.champion?.team_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || season.sport_name.toLowerCase() === selectedSport;
    const matchesStatus = selectedStatus === 'all' || season.status === selectedStatus;
    
    let matchesDecade = selectedDecade === 'all';
    if (!matchesDecade && selectedDecade) {
      const decade = decades.find(d => d.value === selectedDecade);
      if (decade) {
        matchesDecade = season.start_year >= decade.startYear && season.start_year <= decade.endYear;
      }
    }
    
    return matchesSearch && matchesSport && matchesStatus && matchesDecade;
  }) || [];

  const sports = Array.from(new Set(seasons?.data?.map(s => s.sport_name) || []));
  const activeSeasons = filteredSeasons.filter(season => season.status === 'active');
  const completedSeasons = filteredSeasons.filter(season => season.status === 'completed');
  const upcomingSeasons = filteredSeasons.filter(season => season.status === 'upcoming');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'completed': return 'text-gray-400';
      case 'upcoming': return 'text-primary';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'completed': return 'badge-secondary';
      case 'upcoming': return 'badge-primary';
      default: return 'badge-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return Activity;
      case 'completed': return CheckCircle2;
      case 'upcoming': return Clock;
      default: return Calendar;
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Season Database
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">ALL </span>
              <span className="neon-text">SEASONS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Current and historical seasons across all major professional sports leagues
            </p>
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8 overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search seasons, leagues, or champions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport?.toLowerCase()}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Seasons</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="upcoming">Upcoming</option>
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedDecade}
                    onChange={(e) => setSelectedDecade(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Years</option>
                    {decades.map(decade => (
                      <option key={decade.value} value={decade.value}>
                        {decade.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Activity className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {activeSeasons.length}
              </div>
              <div className="text-sm text-gray-400">Active Seasons</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <CheckCircle2 className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {completedSeasons.length}
              </div>
              <div className="text-sm text-gray-400">Completed</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {upcomingSeasons.length}
              </div>
              <div className="text-sm text-gray-400">Upcoming</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports</div>
            </motion.div>
          </div>

          {/* Sport Quick Filters - Only show on main site */}
          {site.isMainSite && (
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Filters</h3>
              
              {/* Professional Sports */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSport(selectedSport === 'football' ? 'all' : 'football');
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'football' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">NFL</div>
                  <div className="text-xs text-gray-400">Football</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSport(selectedSport === 'basketball' ? 'all' : 'basketball');
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'basketball' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">NBA</div>
                  <div className="text-xs text-gray-400">Basketball</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSport(selectedSport === 'baseball' ? 'all' : 'baseball');
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'baseball' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">MLB</div>
                  <div className="text-xs text-gray-400">Baseball</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedSport(selectedSport === 'hockey' ? 'all' : 'hockey');
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'hockey' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">NHL</div>
                  <div className="text-xs text-gray-400">Hockey</div>
                </button>
              </div>
              
              {/* College Sports */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedSport === 'college-football') {
                      setSelectedSport('all');
                    } else {
                      setSelectedSport('football');
                      // Note: Would need to add level filter for true college filtering
                    }
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'college-football' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">CFB</div>
                  <div className="text-xs text-gray-400">College Football</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedSport === 'college-basketball') {
                      setSelectedSport('all');
                    } else {
                      setSelectedSport('basketball');
                      // Note: Would need to add level filter for true college filtering
                    }
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'college-basketball' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">CBB</div>
                  <div className="text-xs text-gray-400">College Basketball</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedSport === 'college-baseball') {
                      setSelectedSport('all');
                    } else {
                      setSelectedSport('baseball');
                      // Note: Would need to add level filter for true college filtering
                    }
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'college-baseball' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">CBS</div>
                  <div className="text-xs text-gray-400">College Baseball</div>
                </button>
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedSport === 'college-hockey') {
                      setSelectedSport('all');
                    } else {
                      setSelectedSport('hockey');
                      // Note: Would need to add level filter for true college filtering
                    }
                  }}
                  className={`glass-card p-4 text-center hover:border-primary/50 transition-all cursor-pointer ${
                    selectedSport === 'college-hockey' ? 'border-primary bg-primary/10' : ''
                  }`}
                >
                  <div className="text-lg font-bold text-white mb-1">CHK</div>
                  <div className="text-xs text-gray-400">College Hockey</div>
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Seasons Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : error ? (
          <div className="glass-card p-12 text-center">
            <Calendar className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-white mb-2">Error Loading Seasons</h3>
            <p className="text-gray-400">{(error as Error).message || 'An error occurred'}</p>
          </div>
        ) : filteredSeasons.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Seasons Found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {filteredSeasons.map((season) => {
              const StatusIcon = getStatusIcon(season.status);
              return (
                <motion.div
                  key={season.id}
                  variants={item}
                  whileHover={{ scale: 1.02 }}
                  className="group"
                >
                  <Link 
                    to={`/seasons/${season.id}`}
                    className="block h-full"
                  >
                    <div className="glass-card h-full overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]">
                      {/* Season Header */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                            <StatusIcon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <span className="badge badge-primary text-xs mr-2">
                              {season.sport_name}
                            </span>
                            <span className={`badge ${getStatusBadge(season.status)} text-xs`}>
                              {season.status.charAt(0).toUpperCase() + season.status.slice(1)}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                      </div>

                      {/* Season Title */}
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                        {season.name}
                      </h3>
                      
                      <p className="text-gray-400 text-sm mb-4">
                        {season.league_name} • {season.level_name}
                      </p>

                      {/* Champion (if completed) */}
                      {season.champion && (
                        <div className="flex items-center space-x-2 mb-4">
                          <Trophy className="w-4 h-4 text-primary" />
                          <span className="text-sm text-primary font-semibold">
                            Champion: {season.champion.team_city} {season.champion.team_name}
                          </span>
                        </div>
                      )}

                      {/* Quick Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Teams
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {season.teams_count || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Games
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {season.games_count || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Duration
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {season.start_year === season.end_year ? 
                              season.start_year : 
                              `${season.start_year}-${String(season.end_year).slice(-2)}`
                            }
                          </div>
                        </div>
                      </div>

                      {/* Set as Active Season Button */}
                      {season.start_year !== currentSeason && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCurrentSeason(season.start_year);
                            }}
                            className="w-full px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 rounded-lg text-primary text-sm font-semibold transition-all duration-200"
                          >
                            Set as Active Season
                          </button>
                        </div>
                      )}
                      {season.start_year === currentSeason && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <div className="flex items-center justify-center space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-success" />
                            <span className="text-sm text-success font-semibold">Active Season</span>
                          </div>
                        </div>
                      )}
                    </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SeasonsPage;